import type { AppDefinition } from "@/client/core/Core.api";
import type { ApiEndpoint } from "@/shared/api-endpoint";
import { kAceStepEndpoint } from "@/shared/ace-step.api";
import { kAudioDescriptionEndpoint } from "@/shared/audio-description.api";
import { kBlend30Endpoint } from "@/shared/blend-3.0.api";
import { kBlendEndpoint } from "@/shared/blend.api";
import { kBpmClassifierEndpoint } from "@/shared/bpm-classifier.api";
import { kBpmQuantizeEndpoint } from "@/shared/bpm-quantize.api";
import { kCraftEndpoint } from "@/shared/craft.api";
import { kDriverSelectorEndpoint } from "@/shared/driver-selector.api";
import { kInpainting40Endpoint } from "@/shared/inpainting-4.0.api";
import { kInpaintingMagicFit40Endpoint } from "@/shared/inpainting-magic-fit-4.0.api";
import { kInpaintingMagicFit40FastEndpoint } from "@/shared/inpainting-magic-fit-4.0-fast.api";
import { kInpaintingMagicFitEndpoint } from "@/shared/inpainting-magic-fit.api";
import { kInpaintingEndpoint } from "@/shared/inpainting.api";
import { kKeyChangerEndpoint } from "@/shared/key-changer.api";
import { kLoopToKitEndpoint } from "@/shared/loop-to-kit.api";
import {
    kLyriaNoteEditEndpoint,
    kLyriaNoteInstructEndpoint,
    kLyriaTranscribeEndpoint,
} from "@/shared/lyria.api";
import { kMagicFit25Endpoint } from "@/shared/magic-fit-2.5.api";
import { kMagicFit30Endpoint } from "@/shared/magic-fit-3.0.api";
import { kMagicFit35Endpoint } from "@/shared/magic-fit-3.5.api";
import { kMagicFit40Endpoint } from "@/shared/magic-fit-4.0.api";
import { kMagicFit40FastEndpoint } from "@/shared/magic-fit-4.0-fast.api";
import {
    kAddStackLayerEndpoint,
    kAddStackLoopEndpoint,
    kNewStackEndpoint,
    kResolveSeedEndpoint,
    kSaveStackEndpoint,
    kStackLayerTypesEndpoint,
    kStackTemplatesEndpoint,
    kSwapStackLayerEndpoint,
} from "@/shared/stacks.api";
import { kStemSplitterEndpoint } from "@/shared/stem-splitter.api";
import {
    kDrumLoopTranscriptionEndpoint,
    kMuscriptorEndpoint,
    kSheetSage2Endpoint,
} from "@/shared/transcription-models.api";
import { kVariations20Endpoint } from "@/shared/variations-2.0.api";
import { kVariations25Endpoint } from "@/shared/variations-2.5.api";
import {
    kSlapConceptsDecomposeEndpoint,
    kSlapConceptsSearchEndpoint,
    kVariations30SlapEndpoint,
} from "@/shared/variations-3.0-slap.api";
import { kVariations30Endpoint } from "@/shared/variations-3.0.api";
import { kVariations40Endpoint } from "@/shared/variations-4.0.api";
import {
    aToBApp,
    allCapabilitiesApp,
    blend30App,
    blendApp,
    craftApp,
    customComponentsApp,
    deepEditDescribeApp,
    deepEditMidiApp,
    designSystemApp,
    exampleApp,
    inpainting40App,
    inpaintingApp,
    inpaintingMagicFit40App,
    inpaintingMagicFitApp,
    interplayApp,
    keyChangerApp,
    loopToDrumKitApp,
    magicFit25App,
    magicFit30App,
    magicFit40App,
    riffApp,
    spliceSamplerApp,
    stacksApp,
    steeredVariationsApp,
    stemEditorApp,
    transcriptionCompareApp,
    variations20App,
    variations25App,
    variations30App,
    variations40App,
    variationsCompare2530App,
    variationsCompareApp,
    voiceToSoundDrumsApp,
} from "./AppDefinitions";

/*
 * Which model endpoints each app runs.
 *
 * This is the first step of a model-hosting process (docs/09-model-hosting.md):
 * an endpoint says whether it is evaluation only, and this says which apps run
 * it, so the R&D Only tag on a card or a header follows from data that can be
 * checked rather than from someone remembering. `app-models.test.ts` fails when
 * a deployed app has no entry here, and when an app's source reaches an
 * evaluation-only endpoint its entry does not list.
 *
 * It lives in its own file because AppDefinitions.ts is imported by
 * vite.config.ts before the `@/` alias exists, so it cannot import shared/.
 *
 * List the models an app runs, not the plumbing around them: storage,
 * playback and Splice search are left out.
 */

/** The endpoint fields this inventory reads, so any concrete endpoint fits. */
export type ModelEndpoint = Pick<
    ApiEndpoint<unknown, unknown>,
    "label" | "source" | "isEvaluationOnly"
>;

const kLyriaEndpoints = [
    kLyriaTranscribeEndpoint,
    kLyriaNoteEditEndpoint,
    kLyriaNoteInstructEndpoint,
] as const;

const kTranscriptionEndpoints = [
    kMuscriptorEndpoint,
    kSheetSage2Endpoint,
    kDrumLoopTranscriptionEndpoint,
] as const;

const kSlapEndpoints = [
    kVariations30SlapEndpoint,
    kSlapConceptsDecomposeEndpoint,
    kSlapConceptsSearchEndpoint,
] as const;

// The Lyria editors share one sample-edit backend (client/capabilities), so
// they run the same set of models.
const kSampleEditEndpoints = [
    ...kLyriaEndpoints,
    kStemSplitterEndpoint,
    ...kTranscriptionEndpoints,
    kAudioDescriptionEndpoint,
    ...kSlapEndpoints,
] as const;

export const kAppModels: ReadonlyMap<AppDefinition, readonly ModelEndpoint[]> = new Map<
    AppDefinition,
    readonly ModelEndpoint[]
>([
    // Utilities. All Capabilities shows the tag per capability row instead.
    [exampleApp, []],
    [designSystemApp, []],
    [customComponentsApp, []],
    [allCapabilitiesApp, []],

    // Versioned capability pages: the release the page itself stands for.
    // Pages with a release picker follow the selected release in their header.
    [variations20App, [kVariations20Endpoint]],
    [variations25App, [kVariations25Endpoint]],
    [variations30App, [kVariations30Endpoint]],
    [variations40App, [kVariations40Endpoint]],
    [magicFit25App, [kMagicFit25Endpoint]],
    // Production, except advanced-mode requests, which go to preprod and are
    // internal-only in the UI. See docs/09-model-hosting.md.
    [magicFit30App, [kMagicFit30Endpoint]],
    [magicFit40App, [kMagicFit40Endpoint]],
    [blendApp, [kBlendEndpoint]],
    [blend30App, [kBlend30Endpoint]],
    [inpaintingApp, [kInpaintingEndpoint]],
    [inpainting40App, [kInpainting40Endpoint]],
    [inpaintingMagicFitApp, [kInpaintingMagicFitEndpoint]],
    [inpaintingMagicFit40App, [kInpaintingMagicFit40Endpoint]],
    [craftApp, [kCraftEndpoint]],
    [keyChangerApp, [kKeyChangerEndpoint, kBpmClassifierEndpoint]],

    // Authored apps.
    [stemEditorApp, kSampleEditEndpoints],
    [deepEditMidiApp, kSampleEditEndpoints],
    [deepEditDescribeApp, kSampleEditEndpoints],
    [transcriptionCompareApp, [...kLyriaEndpoints, ...kTranscriptionEndpoints]],
    [
        stacksApp,
        [
            kStackTemplatesEndpoint,
            kNewStackEndpoint,
            kSwapStackLayerEndpoint,
            kAddStackLayerEndpoint,
            kAddStackLoopEndpoint,
            kStackLayerTypesEndpoint,
            kSaveStackEndpoint,
            kResolveSeedEndpoint,
            kMagicFit30Endpoint,
        ],
    ],
    [
        riffApp,
        [
            kAceStepEndpoint,
            kBpmClassifierEndpoint,
            kInpaintingEndpoint,
            kInpaintingMagicFitEndpoint,
            kKeyChangerEndpoint,
            kVariations30Endpoint,
            kBlend30Endpoint,
        ],
    ],
    // Play and A to B have release pickers, and their headers follow the
    // selected release. The card cannot, so the inventory lists the release the
    // page opens on for everyone (3.0, the default in interplay-releases.ts and
    // a-to-b-releases.ts). Listing every release would flag them always,
    // which says nothing; a viewer who picks 4.0 sees the tag in the header.
    [interplayApp, [kVariations30Endpoint, kMagicFit30Endpoint, kBpmClassifierEndpoint]],
    // The driver selector reads each stem's palette and is preprod whatever
    // release is picked, so A to B is evaluation only regardless.
    [aToBApp, [kVariations30Endpoint, kDriverSelectorEndpoint]],
    [loopToDrumKitApp, [kLoopToKitEndpoint]],
    [voiceToSoundDrumsApp, [kBpmClassifierEndpoint, kBpmQuantizeEndpoint, kMagicFit25Endpoint]],
    [steeredVariationsApp, kSlapEndpoints],

    // Kept in the repo but not deployed (see DeployedApps.ts).
    [
        spliceSamplerApp,
        [
            kMagicFit25Endpoint,
            kMagicFit30Endpoint,
            kMagicFit35Endpoint,
            kMagicFit40Endpoint,
            kMagicFit40FastEndpoint,
            kInpaintingMagicFitEndpoint,
            kInpaintingMagicFit40Endpoint,
            kInpaintingMagicFit40FastEndpoint,
        ],
    ],
    [variationsCompareApp, [kVariations20Endpoint, kVariations25Endpoint]],
    [variationsCompare2530App, [kVariations25Endpoint, kVariations30Endpoint]],
]);

/** Whether any model the app runs is evaluation only. */
export const isAppEvaluationOnly = (app: AppDefinition): boolean =>
    (kAppModels.get(app) ?? []).some((endpoint) => endpoint.isEvaluationOnly === true);
